import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getFunctionErrorMessage } from '../lib/functionErrors';
import type { VickMessage, VickConversation } from '../../types';

interface VickChatResponse {
    conversationId: number;
    answer: string;
    sources: VickMessage['sources'];
}

interface UseVickChatReturn {
    messages: VickMessage[];
    conversationId: number | undefined;
    conversations: VickConversation[];
    loadingConversations: boolean;
    sending: boolean;
    error: string | null;
    modo: 'resposta' | 'planejamento';
    setModo: (modo: 'resposta' | 'planejamento') => void;
    sendMessage: (text: string) => Promise<void>;
    resetConversation: () => void;
    loadConversations: () => Promise<void>;
    openConversation: (id: number) => Promise<void>;
    deleteConversation: (id: number) => Promise<boolean>;
}

export function useVickChat(): UseVickChatReturn {
    const [messages, setMessages] = useState<VickMessage[]>([]);
    const [conversationId, setConversationId] = useState<number | undefined>(undefined);
    const [conversations, setConversations] = useState<VickConversation[]>([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modo, setModo] = useState<'resposta' | 'planejamento'>('resposta');

    const sendMessage = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || sending) return;

        setError(null);
        setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
        setSending(true);

        try {
            const { data, error } = await supabase.functions.invoke('vick-chat', {
                body: { message: trimmed, conversationId, modo },
            });
            if (error) throw error;
            if (!data?.success || !data?.data) {
                throw new Error(data?.error || 'Erro ao falar com a Vick.');
            }

            const result = data.data as VickChatResponse;
            setConversationId(result.conversationId);
            setMessages(prev => [...prev, { role: 'assistant', content: result.answer, sources: result.sources }]);
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao falar com a Vick.');
            setError(message);
        } finally {
            setSending(false);
        }
    }, [conversationId, modo, sending]);

    const resetConversation = useCallback(() => {
        setMessages([]);
        setConversationId(undefined);
        setError(null);
    }, []);

    // Histórico: vick_conversations/vick_messages têm RLS restrita ao próprio
    // usuário (user_id = auth.uid()), então dá pra consultar direto pelo client.
    const loadConversations = useCallback(async () => {
        setLoadingConversations(true);
        try {
            const { data, error } = await supabase
                .from('vick_conversations')
                .select('id, titulo, created_at, updated_at')
                .order('updated_at', { ascending: false })
                .limit(30);
            if (error) throw error;
            setConversations(data ?? []);
        } catch (err) {
            console.error('Erro ao carregar histórico da Vick:', err);
        } finally {
            setLoadingConversations(false);
        }
    }, []);

    const openConversation = useCallback(async (id: number) => {
        setError(null);
        setSending(true);
        try {
            const { data, error } = await supabase
                .from('vick_messages')
                .select('role, content, sources')
                .eq('conversation_id', id)
                .order('created_at', { ascending: true });
            if (error) throw error;
            setMessages((data ?? []) as VickMessage[]);
            setConversationId(id);
        } catch (err) {
            console.error('Erro ao abrir conversa da Vick:', err);
            setError('Não foi possível carregar essa conversa.');
        } finally {
            setSending(false);
        }
    }, []);

    const deleteConversation = useCallback(async (id: number): Promise<boolean> => {
        try {
            // .select() força o Postgres a devolver as linhas realmente apagadas —
            // sem isso, uma policy de RLS ausente faz o delete "funcionar" (sem
            // erro) mas apagar 0 linhas, e o app acharia que deu certo.
            const { data, error } = await supabase.from('vick_conversations').delete().eq('id', id).select('id');
            if (error) throw error;
            if (!data || data.length === 0) {
                throw new Error('A conversa não foi excluída (nenhuma linha afetada). Verifique se a permissão de exclusão está configurada no banco.');
            }
            setConversations(prev => prev.filter(c => c.id !== id));
            if (conversationId === id) {
                setMessages([]);
                setConversationId(undefined);
            }
            return true;
        } catch (err) {
            console.error('Erro ao excluir conversa da Vick:', err);
            setError(err instanceof Error ? err.message : 'Não foi possível excluir essa conversa.');
            return false;
        }
    }, [conversationId]);

    return {
        messages,
        conversationId,
        conversations,
        loadingConversations,
        sending,
        error,
        modo,
        setModo,
        sendMessage,
        resetConversation,
        loadConversations,
        openConversation,
        deleteConversation,
    };
}
